import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for fetching vote audit log
 */
export function useVoteAuditLog(proposalId) {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    if (!proposalId) {
      setAuditLog([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        action: 't24_get_vote_audit_log',
        nonce: window.t24Governance.nonce,
        proposal_id: proposalId
      });

      const response = await fetch(
        `${window.t24Governance.ajaxUrl}?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'same-origin'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch audit log');
      }

      const data = await response.json();

      if (data.success && data.data) {
        setAuditLog(data.data.audit_log || []);
      } else {
        throw new Error(data.data?.message || 'Failed to load audit log');
      }
    } catch (err) {
      console.error('Error fetching vote audit log:', err);
      setError(err.message);
      setAuditLog([]);
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  // Initial fetch
  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // Format time ago
  const getTimeAgo = useCallback((timestamp) => {
    const date = new Date(timestamp);
    const seconds = Math.floor((new Date() - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }, []);

  return {
    auditLog,
    loading,
    error,
    refetch: fetchAuditLog,
    getTimeAgo
  };
}
